// js/components/ProductGallery.js
import Delegate from 'ftdomdelegate';
import { ProductVideo } from './product-video.js';
import { ProductModel } from './product-model.js';

export class ProductGallery {
  constructor(element, options) {
    this.element = element;
    this.delegateElement = new Delegate(this.element);
    this.viewInSpaceElement = this.element.querySelector("[data-shopify-xr]");
    this.options = options;
    this.media = {};
    this.previouslySelectedMedia = null;
    this.cosSliderElement = null;
    this._initializeMedia();
    this._attachListeners();
  }

  destroy() {
    for (let mediaId in this.media) {
      if (this.media.hasOwnProperty(mediaId)) {
        this.media[mediaId].destroy();
      }
    }
    if (this._scrollListener) {
      const sliderContainer = this.cosSliderElement?.querySelector('.cos-slider');
      if (sliderContainer) {
        sliderContainer.removeEventListener('scroll', this._scrollListener);
      }
    }
  }

  _attachListeners() {
    this.delegateElement.on("model:played", this._disableDrag.bind(this));
    this.delegateElement.on("video:played", this._disableDrag.bind(this));
    this.delegateElement.on("model:paused", this._enableDrag.bind(this));
    this.delegateElement.on("video:paused", this._enableDrag.bind(this));
    this._setupSliderScrollListener();
  }

  /**
   * This method must be called whenever the variant is changed
   */
  variantHasChanged(newVariant) {
    fastdom.mutate(() => {
      this.productGalleryCellsElements.forEach((cell) => {
        if (cell.hasAttribute("data-group-name")) {
          let groupName = cell.getAttribute("data-group-name");
          this.options["productOptions"].forEach((option, optionIndex) => {
            if (option.toLowerCase() === groupName) {
              if (newVariant[`option${optionIndex + 1}`].toLowerCase() === cell.getAttribute("data-group-value") || newVariant["featured_media"] && newVariant["featured_media"]["id"] === parseInt(cell.getAttribute("data-media-id"))) {
                cell.classList.remove("is-filtered");
              } else {
                cell.classList.add("is-filtered");
              }
            }
          });
        }
      });
      
      if (newVariant && newVariant["featured_media"]) {
        this._scrollToMedia(newVariant["featured_media"]["id"]);
      }
    });
  }

  _initializeMedia() {
    this.cosSliderElement = this.element.querySelector('cos-slider');
    this.productGalleryCellsElements = this.cosSliderElement ? this.cosSliderElement.querySelectorAll('[data-media-id]') : [];
    this.productGalleryCellsElements.forEach((item) => {
      switch (item.getAttribute("data-media-type")) {
        case "external_video":
        case "video":
          this.media[item.getAttribute("data-media-id")] = new ProductVideo(item, this.options["enableVideoLooping"]);
          break;
        case "model":
          this.media[item.getAttribute("data-media-id")] = new ProductModel();
          break;
      }
    });
  }

  _scrollToMedia(mediaId) {
    const mediaElement = this.element.querySelector(`[data-media-id="${mediaId}"]`);
    if (mediaElement) {
      // cos-slider will handle the scrolling
      mediaElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      this._handleMedia(mediaElement);
    }
  }

  _setupSliderScrollListener() {
    if (!this.cosSliderElement) return;
    
    const sliderContainer = this.cosSliderElement.querySelector('.cos-slider');
    if (!sliderContainer) return;

    let scrollTimeout = null;
    this._scrollListener = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        this._handleSlideChange();
      }, 150);
    };

    sliderContainer.addEventListener('scroll', this._scrollListener, { passive: true });
    
    // Handle initial slide
    this._handleSlideChange();
  }

  _handleSlideChange() {
    if (!this.cosSliderElement) return;
    
    const currentIndex = this.cosSliderElement.getCurrentIndex();
    // Get items using the same method as cos-slider
    const sliderItems = this.cosSliderElement.getSliderItems();
    
    if (currentIndex >= 0 && currentIndex < sliderItems.length) {
      const currentItem = sliderItems[currentIndex];
      if (currentItem && currentItem !== this.previouslySelectedMedia) {
        this._handleMedia(currentItem);
      }
    }
  }

  _disableDrag() {
    // No-op: cos-slider handles dragging
  }

  _enableDrag() {
    // No-op: cos-slider handles dragging
  }

  /**
   * This method will handle the given media (for now model and video) to do the appropriate actions (such as launching
   * a video for instance)
   */
  _handleMedia(item) {
    let isInitialLoading = this.previouslySelectedMedia === null;
    if (this.previouslySelectedMedia && this.previouslySelectedMedia !== item) {
      switch (this.previouslySelectedMedia.getAttribute("data-media-type")) {
        case "video":
        case "external_video":
        case "model":
          this.media[this.previouslySelectedMedia.getAttribute("data-media-id")].hasBeenDeselected();
      }
      if (this.previouslySelectedMedia.getAttribute("data-media-type") === "model" && this.viewInSpaceElement) {
        this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", this.viewInSpaceElement.getAttribute("data-shopify-model3d-default-id"));
      }
    }
    switch (item.getAttribute("data-media-type")) {
      case "video":
      case "external_video":
      case "model":
        this.media[item.getAttribute("data-media-id")].hasBeenSelected(isInitialLoading);
        break;
    }
    if (item.getAttribute("data-media-type") === "model" && this.viewInSpaceElement) {
      this.viewInSpaceElement.setAttribute("data-shopify-model3d-id", item.getAttribute("data-media-id"));
    }
    this.previouslySelectedMedia = item;
  }
}
